// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo, useState } from 'react';

import {
  type ElementId,
  type FabricationAction,
  useEvolvePost,
  type MachineElement,
  type WorkOrderWithStateChange,
} from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { hasLengthExactly } from 'helpers/helper-types';

import { useMachineElementActionsModals } from './modalRegistry';

/**
 * The kind of fabrication work an action applies to. Purely a UI concern: the API takes a single
 * {@link FabricationAction} that combines it with the direction.
 */
export type FabricationWorkType = 'Bend' | 'Cut';

const toFabricationAction = (isDoAction: boolean, workType: FabricationWorkType): FabricationAction =>
  isDoAction ? workType : `Undo${workType}`;

/**
 * Callback when a single element is updated.
 *
 * @remarks We prefer a callback over promise handling here because we only want the callback to be called if the user
 * confirms the action. However, the user refusing the action is not an error, so modeling this with a promise rejection
 * would be incorrect.
 */
type OnElementUpdated = (updatedElement: MachineElement) => void;

/**
 * Callback when several elements are updated.
 *
 * @remarks See {@link OnElementUpdated} as to why we prefer a callback over promise handling.
 */
type OnElementsUpdated = (updatedElements: MachineElement[]) => void;
/**
 * The API reports every work order state change an action would cause, but only completing or un-completing a
 * work order is worth interrupting the user over. A work order moving between Not Started and In Progress is
 * routine, so we don't need to alert the user about those changes.
 */
const changesWorkOrderCompletion = (withStateChange: WorkOrderWithStateChange) =>
  withStateChange.newWorkOrderStateName === 'Completed' || withStateChange.workOrderStateName === 'Completed';

/**
 * Marks machine elements as done or unfinished.
 *
 * Both actions first ask the API which work orders they would change the status of, and hold the action back
 * behind a confirmation modal if any of them would be completed or un-completed.
 *
 * Each action takes a callback that receives the updated elements. The callback is only run if the user confirms.
 * See {@link OnElementUpdated} for why we're using a callback instead of handling this with a promise.
 *
 * Do not call directly. Instead, access through context with {@link useMachineElementActions}.
 */
export const useDoUndoMachineElement = () => {
  const [elementsUpdating, setElementsUpdating] = useState<ElementId[]>([]);

  const { openModal } = useMachineElementActionsModals();

  const { apiCall: executeActionApiCall, loading: executingAction } = useEvolvePost(
    'shop/fabrication/element/execute-fabrication-action',
  );
  const { apiCall: previewActionApiCall, loading: previewingAction } = useEvolvePost(
    'shop/fabrication/element/work-orders-affected-by-fabrication-action',
  );

  /** Updates a set of machine element's status. */
  const runAction = useCallback(
    async (isDoAction: boolean, workType: FabricationWorkType, machineElements: MachineElement[]) => {
      try {
        setElementsUpdating(machineElements.map((me) => me.elementId));
        const machineElementIds = machineElements.map((me) => me.elementId);
        const statusText = isDoAction ? 'done' : 'unfinished';
        const updated = await executeActionApiCall({
          action: toFabricationAction(isDoAction, workType),
          machineElementIds,
        });

        notifications.show({
          title: `Successfully marked part${updated.length > 1 ? 's' : ''} as ${statusText}`,
          message: `${hasLengthExactly(updated, 1) ? updated[0].name : `${updated.length} parts`} marked as ${statusText}.`,
          color: 'green',
        });

        return updated;
      } finally {
        setElementsUpdating([]);
      }
    },
    [executeActionApiCall],
  );

  /**
   * Checks first if updating the elements' status also changes the status of a work order.
   * If so, opens a modal to confirm the action.
   * If not, updates the elements.
   **/
  const attemptAction = useCallback(
    async (
      isDoAction: boolean,
      workType: FabricationWorkType,
      machineElements: MachineElement[],
      onElementsUpdated: OnElementsUpdated,
    ): Promise<void> => {
      setElementsUpdating(machineElements.map((me) => me.elementId));

      try {
        const statusChanges = (
          await previewActionApiCall({
            action: toFabricationAction(isDoAction, workType),
            machineElementIds: machineElements.map((me) => me.elementId),
          })
        ).filter(changesWorkOrderCompletion);

        if (statusChanges.length === 0) {
          onElementsUpdated(await runAction(isDoAction, workType, machineElements));
        } else {
          openModal('FabItemsWillUpdateWOModal', {
            isDoAction,
            machineElements,
            changingWOs: statusChanges,
            onConfirm: async () => {
              await runAction(isDoAction, workType, machineElements).then(onElementsUpdated);
            },
          });
        }
      } finally {
        setElementsUpdating([]);
      }
    },
    [previewActionApiCall, runAction, openModal],
  );

  /** Wrapper around {@link attemptAction} that only operates on a single machine element. */
  const attemptSingleAction = useCallback(
    (
      isDoAction: boolean,
      workType: FabricationWorkType,
      machineElement: MachineElement,
      onElementUpdated: OnElementUpdated,
    ): Promise<void> =>
      attemptAction(isDoAction, workType, [machineElement], (updatedElements) => {
        if (hasLengthExactly(updatedElements, 1)) onElementUpdated(updatedElements[0]);
      }),
    [attemptAction],
  );

  /**
   * Marks one element as done, first confirming with the user if that would change a work order's status.
   *
   * The promise does NOT return the updated element. Instead, pass a callback. See {@link onElementUpdated} for
   * details as to why.
   */
  const markElementDone = useCallback(
    (
      machineElement: MachineElement,
      workType: FabricationWorkType,
      onElementUpdated: OnElementUpdated,
    ): Promise<void> => attemptSingleAction(true, workType, machineElement, onElementUpdated),
    [attemptSingleAction],
  );

  /**
   * Marks one element as unfinished, first confirming with the user if that would change a work order's status.
   *
   * The promise does NOT return the updated element. Instead, pass a callback. See {@link OnElementsUpdated} for
   * details as to why.
   */
  const markElementUnfinished = useCallback(
    (
      machineElement: MachineElement,
      workType: FabricationWorkType,
      onElementUpdated: OnElementUpdated,
    ): Promise<void> => attemptSingleAction(false, workType, machineElement, onElementUpdated),
    [attemptSingleAction],
  );

  /**
   * Marks several elements as done, first confirming with the user if that would change any work order's status.
   *
   * The promise does NOT return the updated elements. Instead, pass a callback. See {@link OnElementsUpdated} for
   * details as to why.
   */
  const markElementsDone = useCallback(
    (
      machineElements: MachineElement[],
      workType: FabricationWorkType,
      onElementsUpdated: OnElementsUpdated,
    ): Promise<void> => attemptAction(true, workType, machineElements, onElementsUpdated),
    [attemptAction],
  );

  const updatingSet = useMemo(() => new Set(elementsUpdating), [elementsUpdating]);

  const mutationRunning = executingAction || previewingAction || updatingSet.size > 0;

  /** Checks if a machine element is currently being updated. */
  const checkIfElementIsUpdating = useCallback((elementId: ElementId) => updatingSet.has(elementId), [updatingSet]);

  return useMemo(
    () => ({
      markElementDone,
      markElementsDone,
      markElementUnfinished,
      mutationRunning,
      checkIfElementIsUpdating,
    }),
    [markElementDone, markElementsDone, markElementUnfinished, mutationRunning, checkIfElementIsUpdating],
  );
};
